import {
    createSystem,
    defaultConfig,
    defineConfig,
    defineRecipe,
} from "@chakra-ui/react"
import {
    badgeRecipe as defaultBadgeRecipe,
    buttonRecipe as defaultButtonRecipe,
    cardSlotRecipe as defaultCardSlotRecipe,
    tabsSlotRecipe as defaultTabsSlotRecipe,
} from "@chakra-ui/react/theme"
import { animationStyles } from "./animation-styles"

/* ============================================================
   COLOR PALETTES

   CoHaven's palette is warm, pastel, Mediterranean — cream
   canvas, dusty steel-blue brand, gold accent. The status
   palettes (green/red/orange/yellow/blue/purple/gray) are
   re-shaded to soft pastels so any `colorPalette="green"`
   prop in the app naturally inherits the warm tone.

   Each scale defines 50–950 so Chakra's built-in semantic
   slots (subtle/muted/emphasized/solid/fg/contrast/focusRing)
   resolve correctly in both light and dark mode.
   ============================================================ */

const brand = {
    50: { value: "#eef3f8" },
    100: { value: "#d3dfec" },
    200: { value: "#b7cbe0" },
    300: { value: "#9bb7d4" },
    400: { value: "#7fa3c8" },
    500: { value: "#446c97" }, // primary brand blue
    600: { value: "#3b5f85" },
    700: { value: "#324f70" },
    800: { value: "#283f5a" },
    900: { value: "#1f3044" },
    950: { value: "#131e2b" },
}

// Warm clay-tinted neutral. Replaces Chakra's cool gray so
// borders, muted text and disabled states feel of-a-piece
// with the cream canvas.
const clay = {
    50: { value: "#f7f4ef" },
    100: { value: "#ece6dc" },
    200: { value: "#ddd3c3" },
    300: { value: "#c2b39e" },
    400: { value: "#9e8e78" },
    500: { value: "#7a6c58" },
    600: { value: "#5f5343" },
    700: { value: "#4a4035" },
    800: { value: "#352e26" },
    900: { value: "#23201b" },
    950: { value: "#15130f" },
}

// Dusty olive-sage. Used for active/positive/success states
// (deposits, balances trending up, completed steps).
const sage = {
    50: { value: "#f1f5ed" },
    100: { value: "#dde8d6" },
    200: { value: "#bfd3b3" },
    300: { value: "#9cba8c" },
    400: { value: "#7a9d68" },
    500: { value: "#5e8254" },
    600: { value: "#496941" },
    700: { value: "#3a5435" },
    800: { value: "#2e412a" },
    900: { value: "#22301f" },
    950: { value: "#131c12" },
}

// Warm clay red. Destructive actions, debits, error states.
const terracotta = {
    50: { value: "#fbf0eb" },
    100: { value: "#f4ddd2" },
    200: { value: "#e9bfac" },
    300: { value: "#dda085" },
    400: { value: "#cd7f5e" },
    500: { value: "#b66143" },
    600: { value: "#984e35" },
    700: { value: "#7a3e2c" },
    800: { value: "#5e3023" },
    900: { value: "#46241c" },
    950: { value: "#2a140f" },
}

// Soft amber. Warning states, pending/in-review badges.
const sand = {
    50: { value: "#fbf5e4" },
    100: { value: "#f5e8c2" },
    200: { value: "#ecd690" },
    300: { value: "#e0bf5e" },
    400: { value: "#d2a839" },
    500: { value: "#b88a25" },
    600: { value: "#966e1b" },
    700: { value: "#765616" },
    800: { value: "#5a4214" },
    900: { value: "#443211" },
    950: { value: "#281c08" },
}

// Lighter dusty blue, distinct from `brand`. Use for info
// badges (inbox, feature highlights) where brand would be
// too heavy.
const sky = {
    50: { value: "#eef4f8" },
    100: { value: "#d4e2ec" },
    200: { value: "#b3cadc" },
    300: { value: "#8db0c7" },
    400: { value: "#6695b3" },
    500: { value: "#487a99" },
    600: { value: "#3a637e" },
    700: { value: "#2f4f64" },
    800: { value: "#253e4d" },
    900: { value: "#1d2f3a" },
    950: { value: "#111b22" },
}

// Dusty muted purple. Creator/admin role badges, premium tags.
const lavender = {
    50: { value: "#f3eff5" },
    100: { value: "#e3dae8" },
    200: { value: "#c8b6d2" },
    300: { value: "#ac91bc" },
    400: { value: "#906da4" },
    500: { value: "#76558a" },
    600: { value: "#5e4470" },
    700: { value: "#4a3658" },
    800: { value: "#382a43" },
    900: { value: "#281f30" },
    950: { value: "#18121d" },
}

// Warm orange (sits between sand and terracotta). Used for
// expense/debit indicators; distinct from `terracotta` which
// reads more red.
const tangerine = {
    50: { value: "#fbeee2" },
    100: { value: "#f6dac0" },
    200: { value: "#ecb787" },
    300: { value: "#dd9355" },
    400: { value: "#cb7a3a" },
    500: { value: "#b06026" },
    600: { value: "#8e4c1d" },
    700: { value: "#6f3c18" },
    800: { value: "#532d14" },
    900: { value: "#3c220f" },
    950: { value: "#221207" },
}

// Soft mint-teal. Balance / financial-positive surfaces
// (the "Balance" stat on the home overview).
const teal = {
    50: { value: "#eaf3f0" },
    100: { value: "#cfe3dc" },
    200: { value: "#a8ccc1" },
    300: { value: "#7eb3a3" },
    400: { value: "#569885" },
    500: { value: "#3a7e6b" },
    600: { value: "#2d6555" },
    700: { value: "#244f43" },
    800: { value: "#1c3d35" },
    900: { value: "#152e28" },
    950: { value: "#0a1915" },
}

// Warm metallic gold. Accent palette for highlighted CTAs,
// premium moments, and editorial emphasis.
const gold = {
    50: { value: "#faf6ec" },
    100: { value: "#f2e6c8" },
    200: { value: "#e8d19d" },
    300: { value: "#ddbb70" },
    400: { value: "#d8b268" },
    500: { value: "#c9a24d" },
    600: { value: "#a8842f" },
    700: { value: "#8a6e24" },
    800: { value: "#5f4722" },
    900: { value: "#423119" },
    950: { value: "#281c0d" },
}

/* ============================================================
   COMPONENT RECIPES
   ============================================================ */

const buttonRecipe = defineRecipe({
    ...defaultButtonRecipe,
    base: {
        ...defaultButtonRecipe.base,
        borderRadius: "full",
    },
    defaultVariants: {
        ...(defaultButtonRecipe.defaultVariants ?? {}),
        colorPalette: "gray",
    },
})

const badgeRecipe = defineRecipe({
    ...defaultBadgeRecipe,
    defaultVariants: {
        ...(defaultBadgeRecipe.defaultVariants ?? {}),
        colorPalette: "brand",
    },
})

const cardSlotRecipe = {
    ...defaultCardSlotRecipe,
    defaultVariants: { variant: "outline" as const },
    base: {
        ...defaultCardSlotRecipe.base,
        root: {
            ...defaultCardSlotRecipe.base?.root,
            // `2xl` (16px), the Home Hub concept's card radius. Set here rather
            // than per card so the whole app moves together — the concept draws
            // its panels at 16–18px, and 12px read as a different, older
            // surface beside anything already on the newer scale.
            borderRadius: "2xl",
            transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        },
        body: {
            ...defaultCardSlotRecipe.base?.body,
            padding: { base: 4, sm: 5, md: 6 },
        },
    },
    variants: {
        ...defaultCardSlotRecipe.variants,
        variant: {
            ...defaultCardSlotRecipe.variants?.variant,
            outline: {
                root: {
                    bg: "bg.panel",
                    borderWidth: "1px",
                    borderColor: "border.card",
                },
            },
        },
    },
} as unknown as typeof defaultCardSlotRecipe

const tabsSlotRecipe = {
    ...defaultTabsSlotRecipe,
    variants: {
        ...defaultTabsSlotRecipe.variants,
        variant: {
            ...(defaultTabsSlotRecipe.variants?.variant ?? {}),
            line: {
                list: {
                    display: "flex",
                    borderColor: "border",
                    _horizontal: { borderBottomWidth: "1px" },
                    _vertical: { borderEndWidth: "1px" },
                },
                trigger: {
                    color: "fg.muted",
                    _disabled: { _active: { bg: "initial" } },
                    _selected: {
                        color: "gold.500",
                        _horizontal: {
                            layerStyle: "indicator.bottom",
                            "--indicator-offset-y": "-1px",
                            "--indicator-color": "colors.gold.500",
                        },
                        _vertical: {
                            layerStyle: "indicator.end",
                            "--indicator-offset-x": "-1px",
                        },
                    },
                },
            },
        },
    },
} as unknown as typeof defaultTabsSlotRecipe

/* ============================================================
   THEME CONFIG
   ============================================================ */

const config = defineConfig({
    globalCss: {
        "*:focus:not(:focus-visible)": {
            outline: "none",
            boxShadow: "none",
        },
    },
    theme: {
        recipes: {
            button: buttonRecipe,
            badge: badgeRecipe,
        },
        slotRecipes: {
            card: cardSlotRecipe,
            tabs: tabsSlotRecipe,
        },
        animationStyles,
        tokens: {
            colors: {
                // Custom CoHaven palettes
                brand,
                clay,
                sage,
                terracotta,
                sand,
                sky,
                lavender,
                tangerine,
                teal,
                gold,

                gray: clay,
                green: sage,
                red: terracotta,
                yellow: sand,
                blue: sky,
                purple: lavender,
                orange: tangerine,
                white: { value: "#ffffff" },

                cream: { value: "#EFE9DF" },
                dark: { value: "#1F1F1F" },
            },
            fonts: {
                heading: { value: "'Manrope', sans-serif" },
                body: { value: "'Manrope', sans-serif" },
                // Machine-ish text a person reads back to themselves — the
                // home's inbound email address. Loaded in index.html.
                mono: { value: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" },
            },
            radii: {
                card: { value: "0.75rem" }, // 12px
            },
            gradients: {
                hubHero: {
                    value:
                        "linear-gradient(100deg, {colors.bg.panel} 0%, {colors.bg.heroMid} 52%, {colors.bg.heroTo} 100%)",
                },
            },
        },

        /* --------------------------------------------------------
           SEMANTIC TOKENS

           These are the names used in component code (`bg.panel`,
           `fg.muted`, `border.card`). Each has a light + dark
           value so the whole app respects color mode automatically.
           -------------------------------------------------------- */
        semanticTokens: {
            colors: {
                primary: {
                    value: {
                        _light: "{colors.dark}",
                        _dark: "{colors.cream}",
                    },
                },
                gold: {
                    contrast: { value: { _light: "white", _dark: "white" } },
                    fg: {
                        value: {
                            _light: "{colors.gold.700}",
                            _dark: "{colors.gold.300}",
                        },
                    },
                    subtle: {
                        value: {
                            _light: "{colors.gold.100}",
                            _dark: "{colors.gold.900}",
                        },
                    },
                    muted: {
                        value: {
                            _light: "{colors.gold.200}",
                            _dark: "{colors.gold.800}",
                        },
                    },
                    emphasized: {
                        value: {
                            _light: "{colors.gold.300}",
                            _dark: "{colors.gold.700}",
                        },
                    },
                    solid: {
                        value: {
                            _light: "{colors.gold.600}",
                            _dark: "{colors.gold.600}",
                        },
                    },
                    focusRing: {
                        value: {
                            _light: "{colors.gold.500}",
                            _dark: "{colors.gold.500}",
                        },
                    },
                    border: {
                        value: {
                            _light: "{colors.gold.500}",
                            _dark: "{colors.gold.400}",
                        },
                    },

                    wash: {
                        value: { _light: "rgba(201,162,77,0.16)", _dark: "rgba(201,162,77,0.24)" },
                    },
                    washHover: {
                        value: { _light: "rgba(201,162,77,0.14)", _dark: "rgba(201,162,77,0.20)" },
                    },
                    washMuted: {
                        value: { _light: "rgba(201,162,77,0.10)", _dark: "rgba(201,162,77,0.16)" },
                    },
                    washFaint: {
                        value: { _light: "rgba(201,162,77,0.07)", _dark: "rgba(201,162,77,0.12)" },
                    },
                    washGhost: {
                        value: { _light: "rgba(201,162,77,0.05)", _dark: "rgba(201,162,77,0.10)" },
                    },
                    hairline: {
                        value: { _light: "rgba(201,162,77,0.50)", _dark: "rgba(201,162,77,0.55)" },
                    },
                    hairlineSoft: {
                        value: { _light: "rgba(201,162,77,0.45)", _dark: "rgba(201,162,77,0.50)" },
                    },
                    // Icon stroke and label on a gold tint. Distinct from
                    // `gold.fg` only in intent — it names the job, so the two
                    // can diverge without a sweep.
                    ink: {
                        value: { _light: "{colors.gold.700}", _dark: "{colors.gold.300}" },
                    },
                    // "View all", "+ Add event" — gold as a link.
                    link: {
                        value: { _light: "{colors.gold.600}", _dark: "{colors.gold.300}" },
                    },
                    // The mobile add button's face. Lighter than `gold.solid`
                    // because it carries a charcoal glyph, not a white one.
                    fab: {
                        value: { _light: "#DFC896", _dark: "{colors.gold.400}" },
                    },
                },
                bg: {
                    DEFAULT: {
                        value: { _light: "#FDFAF5", _dark: "#2A2622" },
                    },
                    panel: {
                        value: { _light: "#FDFAF5", _dark: "#2A2622" },
                    },
                    canvas: {
                        value: { _light: "{colors.cream}", _dark: "{colors.dark}" },
                    },
                    input: {
                        value: { _light: "#FFFDF9", _dark: "#332F2A" },
                    },
                    subtle: {
                        value: { _light: "#F5EFE4", _dark: "#332F2A" },
                    },
                    muted: {
                        value: { _light: "#EBE5D7", _dark: "#3A352E" },
                    },
                    emphasized: {
                        value: { _light: "#E2D9C7", _dark: "#443E35" },
                    },
                    inverted: {
                        value: { _light: "{colors.clay.900}", _dark: "{colors.cream}" },
                    },
                    light: {
                        value: { _light: "{colors.white}", _dark: "{colors.clay.900}" },
                    },
                    tile: {
                        value: { _light: "#F2EBDF", _dark: "#3A352E" },
                    },
                    chip: {
                        value: { _light: "#E4DCD0", _dark: "#443E35" },
                    },
                    rowHover: {
                        value: { _light: "rgba(31,31,31,0.045)", _dark: "rgba(255,255,255,0.06)" },
                    },
                    bar: {
                        value: { _light: "rgba(31,31,31,0.12)", _dark: "rgba(255,255,255,0.16)" },
                    },
                    heroMid: {
                        value: { _light: "#FAF4EA", _dark: "#302B25" },
                    },
                    heroTo: {
                        value: { _light: "#F3E9DA", _dark: "#38312A" },
                    },

                    band: {
                        value: { _light: "{colors.dark}", _dark: "#171717" },
                    },
                },

                fg: {
                    DEFAULT: {
                        value: { _light: "#1a1a1a", _dark: "#f0ece6" },
                    },
                    emphasized: {
                        value: { _light: "#111111", _dark: "#ffffff" },
                    },
                    muted: {
                        value: { _light: "#6b5f55", _dark: "{colors.clay.300}" },
                    },
                    subtle: {
                        value: { _light: "#9a8f83", _dark: "{colors.clay.400}" },
                    },
                    error: {
                        value: {
                            _light: "{colors.terracotta.700}",
                            _dark: "{colors.terracotta.300}",
                        },
                    },
                    inverted: {
                        value: { _light: "#ffffff", _dark: "#1a1a1a" },
                    },
                    nav: {
                        value: { _light: "rgba(31,31,31,0.72)", _dark: "rgba(240,236,230,0.72)" },
                    },

                    onBand: {
                        value: { _light: "{colors.cream}", _dark: "{colors.cream}" },
                    },
                    onBandMuted: {
                        value: {
                            _light: "rgba(239,233,223,0.55)",
                            _dark: "rgba(239,233,223,0.55)",
                        },
                    },
                    onBandSubtle: {
                        value: {
                            _light: "rgba(239,233,223,0.42)",
                            _dark: "rgba(239,233,223,0.42)",
                        },
                    },
                },

                border: {
                    DEFAULT: {
                        value: { _light: "#DDD6CE", _dark: "#444444" },
                    },
                    subtle: {
                        value: { _light: "#ECE8E2", _dark: "#3A3A3A" },
                    },
                    muted: {
                        value: { _light: "#E5DFD7", _dark: "#3F3F3F" },
                    },
                    emphasized: {
                        value: { _light: "#C8C0B8", _dark: "#555555" },
                    },
                    card: {
                        value: { _light: "#E0D8CB", _dark: "#3A3A3A" },
                    },
                    chrome: {
                        value: { _light: "rgba(31,31,31,0.09)", _dark: "rgba(255,255,255,0.11)" },
                    },
                    // Divider inside `bg.band`. Fixed like the band itself.
                    band: {
                        value: {
                            _light: "rgba(239,233,223,0.14)",
                            _dark: "rgba(239,233,223,0.14)",
                        },
                    },
                },

                onboarding: {
                    accentSoft: {
                        value: {
                            _light: "{colors.sand.50}",
                            _dark: "{colors.sand.900}",
                        },
                    },
                },
            },
        },
    },
})

export const system = createSystem(defaultConfig, config)
